"use client";

import { useLocalStorage } from "usehooks-ts";
import { memo, type ElementType } from "react";
import {
  SignedIn,
  SignedOut,
  SignInButton,
  UserButton,
  useAuth,
  useUser,
} from "@clerk/nextjs";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import {
  HomeIcon,
  MagnifyingGlassIcon,
  StarIcon,
  BellAlertIcon,
  WrenchScrewdriverIcon,
  Bars3Icon,
  InboxIcon,
  NewspaperIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Header as HeaderBase } from "@/app/_components/header";
import { Logo } from "@/app/_components/logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import { cn } from "@/lib/utils";

import { getUserPlanByMetadata } from "@/helper/user/client";

import { phoneSupport, plans, storageKeys } from "@/constants";

import type { Plans } from "@/types";

interface NavItem {
  href: string;
  icon: ElementType;
  label?: string;
  visible: boolean;
}

const navItems: NavItem[] = [
  {
    href: "/app/inicio",
    icon: HomeIcon,
    label: "Painel",
    visible: false,
  },
  {
    href: "/leilao-de-imoveis",
    icon: MagnifyingGlassIcon,
    label: "Buscar imóveis",
    visible: true,
  },
  {
    href: "/app/favoritos",
    icon: StarIcon,
    label: "Favoritos",
    visible: true,
  },
  {
    href: "/app/alertas",
    icon: BellAlertIcon,
    label: "Alertas",
    visible: true,
  },
  {
    href: "/app/ferramentas",
    icon: WrenchScrewdriverIcon,
    label: "Ferramentas",
    visible: true,
  },
  {
    href: "/app/matriculas",
    icon: NewspaperIcon,
    label: "Matrículas",
    visible: true,
  },
];

interface NavItemProps extends NavItem {
  isActive: boolean;
  showBadge?: boolean;
}

const NavItem = ({
  href,
  icon: Icon,
  label,
  isActive,
  showBadge,
}: NavItemProps) => (
  <Link
    prefetch={false}
    href={href}
    className={cn(
      "flex items-center justify-center rounded-full px-4 py-2 text-sm transition-colors hover:text-primary relative",
      isActive ? "bg-muted font-medium text-primary" : "text-muted-foreground"
    )}
  >
    {label ? (
      <>
        <Icon
          aria-hidden="true"
          className={cn("h-4 w-4 mr-2", isActive ? "" : "hidden")}
        />
        {label}
      </>
    ) : (
      <>
        <Icon aria-hidden="true" className="h-4 w-4" />
        {showBadge && (
          <Badge
            variant="destructive"
            className="absolute top-[5px] right-[13px] h-[9px] w-[9px] p-0"
            aria-label="Onboarding pendente"
          />
        )}
      </>
    )}
  </Link>
);

const Nav = () => {
  const { isSignedIn } = useAuth();
  const pathname = usePathname();

  if (!isSignedIn) return null;

  const items = navItems.filter((item) => item.visible);

  return (
    <nav className="flex-1 text-right sm:flex-none sm:text-center">
      <div className="hidden sm:flex sm:flex-row sm:gap-1">
        {items.map((item) => (
          <NavItem
            key={item.href}
            {...item}
            isActive={pathname.includes(item.href)}
          />
        ))}
      </div>
      <div className="sm:hidden">
        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="shrink-0 sm:hidden"
              aria-label="Abrir menu"
            >
              <Bars3Icon className="h-5 w-5" />
              <span className="sr-only">Abrir menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent
            aria-describedby={undefined}
            side="left"
            className="flex flex-col h-full overflow-auto w-4/6"
          >
            <VisuallyHidden asChild>
              <SheetTitle>Pro Leilão Menu</SheetTitle>
            </VisuallyHidden>
            <nav className="grid gap-2 text-lg font-medium">
              <div className="flex items-center gap-2 mb-6">
                <Logo height={25} width={25} />
                <span className="font-semibold">Pro Leilão</span>
              </div>
              {items.map((item) => (
                <NavItem
                  key={item.href}
                  {...item}
                  isActive={pathname.includes(item.href)}
                />
              ))}
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
};

export const Header = memo(() => {
  const router = useRouter();
  const pathname = usePathname();
  const { user } = useUser();
  const { plan, hasPaidPlan } = getUserPlanByMetadata(user?.publicMetadata);

  const [onboardingProfileCompleted] = useLocalStorage(
    storageKeys.onboardingProfileCompleted,
    "pending",
    { initializeWithValue: false }
  );
  const [onboardingFeaturesCompleted] = useLocalStorage(
    storageKeys.onboardingFeaturesCompleted,
    "pending",
    { initializeWithValue: false }
  );

  const onboardingComplete =
    onboardingProfileCompleted === "done" &&
    onboardingFeaturesCompleted === "done";

  const openWhatsApp = () => {
    window.open(
      `https://wa.me/${phoneSupport}?text=Olá,%20estou%20entrando%20em%20contato%20atráves%20do%20Pro%20Leilão...`,
      "_blank"
    );
  };

  const openOnboarding = () => {
    router.push("/app/primeiros-passos");
  };

  const openSubscription = () => {
    router.push("/app/assinatura");
  };

  return (
    <HeaderBase>
      <Nav />
      <div className="flex flex-row">
        <SignedOut>
          <div>
            <SignInButton>
              <Button variant="outline">Entrar</Button>
            </SignInButton>
          </div>
        </SignedOut>
        <div className="flex flex-row gap-2 items-center justify-end">
          {user ? (
            <NavItem
              href="/app/primeiros-passos"
              icon={InboxIcon}
              visible
              isActive={pathname.includes("/app/primeiros-passos")}
              showBadge={!onboardingComplete}
            />
          ) : null}
          <SignedIn>
            <div className="h-7 w-7">
              <UserButton userProfileMode="modal">
                <UserButton.MenuItems>
                  <UserButton.Action
                    label={`Gerenciar assinatura (Plano ${plan})`}
                    labelIcon={
                      <img
                        loading="eager"
                        src={plans[plan as Plans].icon}
                        alt={`Plano ${plan}`}
                        height={16}
                        width={16}
                      />
                    }
                    onClick={openSubscription}
                  />
                  {hasPaidPlan ? (
                    <UserButton.Action
                      label="Suporte via WhatsApp"
                      labelIcon={<PhoneIcon className="w-4 h-4" />}
                      onClick={openWhatsApp}
                    />
                  ) : null}
                  <UserButton.Action
                    label="Primeiros passos"
                    labelIcon={<InboxIcon className="w-4 h-4" />}
                    onClick={openOnboarding}
                  />
                </UserButton.MenuItems>
              </UserButton>
            </div>
          </SignedIn>
        </div>
      </div>
    </HeaderBase>
  );
});

Header.displayName = "Header";
